<template>
  <figure ref="figure" class="part-spot" :class="[ 'is-' + kind, { 'is-flying': flying } ]" :key="kind + image" :aria-label="kind === 'tyres' ? 'Tyre details' : 'Wheel details'">
    <button type="button" class="part-spot--close" :aria-label="`Close ${ kind === 'tyres' ? 'tyre' : 'wheel' } details`" title="Close" @click="$emit( 'close' )">
      <v-icon small>mdi-close</v-icon>
    </button>
    <div class="part-spot--side">
      <div v-if="data.lead" class="part-spot--fig is-lead">
        <small>{{ data.lead.label }}</small><b>{{ data.lead.value }}</b>
      </div>
      <div v-for="fig in data.side" :key="fig.label" class="part-spot--fig has-tick">
        <small>{{ fig.label }}</small><b>{{ fig.value }}</b>
      </div>
    </div>
    <div class="part-spot--part">
      <div v-if="data.top" class="part-spot--fig is-top">
        <small>{{ data.top.label }}</small><b>{{ data.top.value }}</b>
      </div>
      <div class="part-spot--pic" :class="{ 'is-photo': !!data.photo }">
        <!-- Tyres: the maker's tread shot when we have one; otherwise the part as the bike draws it. -->
        <img v-if="data.photo" class="part-spot--img" :src="data.photo" :alt="`Tread of the ${ data.name }`" decoding="async" :title="`Choose ${ kind }`" @load="flyIn" @click="$emit( 'open' )">
        <img v-else-if="image" class="part-spot--img" :src="image" alt="" decoding="async" :title="`Choose ${ kind }`" @load="flyIn" @click="$emit( 'open' )">
        <span v-else class="part-spot--placeholder" aria-hidden="true"></span>
      </div>
      <div v-if="data.bottom" class="part-spot--fig is-bottom">
        <small>{{ data.bottom.label }}</small><b>{{ data.bottom.value }}</b>
      </div>
    </div>
  </figure>
</template>

<script>
  export default {
    props: {
      // 'wheels' or 'tyres'.
      kind: { type: String, required: true },
      // The part as the bike draws it (Bike partSnapshot); empty while it renders.
      image: { type: String, default: '' },
      // { name, lead, side: [], top, bottom, photo }: each figure { label, value }.
      // photo: a product shot on white (tyre tread); it replaces the snapshot.
      data: { type: Object, required: true },
      // Where the part sits on the bike ({ x, y, size }: centre and diameter,
      // in the preview's pixels): the picture lifts off from there.
      from: { type: Object, default: null }
    },
    // Decided once when the spotlight appears, so later prop changes don't restart animations.
    data() {
      return { flying: !!this.from };
    },
    methods: {
      flyIn( e ) {
        const img = e.target, from = this.from;
        if ( !from || !img.animate ) return;
        const box = this.$refs.figure.getBoundingClientRect(), r = img.getBoundingClientRect();
        if ( !r.width ) return;
        const dx = from.x - ( r.left - box.left + r.width / 2 ), dy = from.y - ( r.top - box.top + r.height / 2 );
        const k = from.size / Math.max( r.width, r.height );
        img.animate([
          { transform: `translate(${ dx }px, ${ dy }px) scale(${ k })`, opacity: .9 },
          { transform: 'none', opacity: 1 }
        ], { duration: 700, easing: 'cubic-bezier(.2, .8, .2, 1)' });
      }
    }
  };
</script>

<style lang="scss">
  /* The chosen wheel or tyre pulled out of the bike, large, with its figures. */
  .part-spot {
    /* Spec-sheet look: hairline ink rules ending in a brand-green dot on the part. */
    --spot-mark: rgba(37, 44, 34, .38);
    --spot-dot: var(--builder-accent, #344b35);
    /* Height of the label rows above and below the part (the part gets the rest). */
    --spot-labels: 124px;
    position: absolute; inset: 0; z-index: 3; margin: 0; pointer-events: none;
    display: flex; align-items: center; justify-content: center; gap: 0; padding: 0 3% 0 30%;
    color: var(--builder-ink, #252c22);
  }
  .part-spot--close {
    position: absolute; top: 12px; right: 14px; z-index: 1; pointer-events: auto;
    display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px;
    border: 1px solid var(--builder-line, #dedfd7); border-radius: 50%; background: rgba(255, 255, 255, .92);
  }
  .part-spot--close .v-icon { color: var(--builder-ink, #252c22); }
  .part-spot--close:hover { background: #fff; }
  .part-spot--close:focus-visible { outline: 2px solid var(--builder-accent, #344b35); outline-offset: 2px; }
  /* Figures sit right against the part: their ticks end on the rim. */
  .part-spot--side { display: flex; flex-direction: column; align-items: flex-end; gap: 18px; text-align: right; animation: part-spot-fade .45s .3s ease-out both; }
  .part-spot--part { position: relative; display: flex; flex-direction: column; align-items: center; height: 84%; animation: part-spot-in .5s cubic-bezier(.2, .8, .2, 1) both; }
  /* The box is exactly the picture (height-led), so ticks and labels meet its edge. */
  .part-spot--pic { position: relative; flex: none; height: calc(100% - var(--spot-labels)); aspect-ratio: 1; display: flex; align-items: center; justify-content: center; }
  .part-spot--placeholder { width: 100%; height: 100%; }
  .part-spot--img { width: 100%; height: 100%; object-fit: contain; pointer-events: auto; cursor: pointer; filter: drop-shadow(0 18px 24px rgba(37, 44, 34, .18)); animation: part-spot-fade .25s ease-out both; }
  /* Tread shots are cut out (transparent background), sized to the frame. */
  /* The box takes the photo's own shape, so the labels above and below hug it. */
  .part-spot--pic.is-photo { aspect-ratio: auto; height: auto; max-height: calc(100% - var(--spot-labels)); width: min(34vw, 520px); }
  .part-spot--pic.is-photo .part-spot--img { width: 100%; height: auto; max-height: 100%; aspect-ratio: auto; filter: drop-shadow(0 18px 22px rgba(37, 44, 34, .16)); }
  .part-spot--part:has(.is-photo) { justify-content: center; }
  .part-spot--fig { display: flex; flex-direction: column; line-height: 1.1; white-space: nowrap; text-shadow: 0 0 16px var(--builder-beige, #f6f5f1), 0 0 8px var(--builder-beige, #f6f5f1), 0 0 3px var(--builder-beige, #f6f5f1); }
  .part-spot--fig small { margin-bottom: 4px; font-size: 11.5px; font-weight: 500; letter-spacing: .12em; text-transform: uppercase; color: #4c5448; }
  .part-spot--fig b { font-size: 26px; font-weight: 400; letter-spacing: -.01em; font-variant-numeric: tabular-nums; }
  .part-spot--fig.is-lead b { font-size: 44px; letter-spacing: -.02em; }
  .part-spot--fig.is-top, .part-spot--fig.is-bottom { align-items: center; animation: part-spot-fade .45s .2s ease-out both; }
  .part-spot--fig.is-top::after, .part-spot--fig.is-bottom::before { content: ''; width: 1px; height: 22px; margin: 6px 0; background: var(--spot-mark); }
  .part-spot--fig.has-tick { position: relative; padding-right: 40px; }
  .part-spot--fig.has-tick::after { content: ''; position: absolute; right: 3px; top: 50%; width: 34px; height: 1px; background: var(--spot-mark); }
  .part-spot--fig.has-tick::before { content: ''; position: absolute; right: 0; top: 50%; width: 6px; height: 6px; margin-top: -2.5px; border-radius: 50%; background: var(--spot-dot); box-shadow: 0 0 0 2px var(--builder-beige, #f6f5f1); }
  .part-spot--fig.is-lead { padding-right: 40px; }
  /* Flying out of the bike: the picture animates itself (flyIn); the rest fades in. */
  .part-spot.is-flying .part-spot--part { animation: none; }
  .part-spot.is-flying .part-spot--fig.is-top, .part-spot.is-flying .part-spot--fig.is-bottom { animation-delay: .45s; }
  @keyframes part-spot-in { from { opacity: 0; transform: translateX(-35%) scale(.75); } to { opacity: 1; transform: none; } }
  @keyframes part-spot-fade { from { opacity: 0; } to { opacity: 1; } }
  @media (max-width: 700px) {
    .part-spot { padding: 0 3% 0 26%; --spot-labels: 74px; }
    .part-spot--close { top: 6px; right: 8px; width: 30px; height: 30px; }
    .part-spot--side { gap: 10px; }
    .part-spot--fig small { margin-bottom: 2px; font-size: 9.5px; letter-spacing: .08em; }
    .part-spot--pic.is-photo { width: 44vw; }
    .part-spot--fig b { font-size: 15px; }
    .part-spot--fig.is-lead b { font-size: 24px; }
    .part-spot--fig.is-top::after, .part-spot--fig.is-bottom::before { height: 10px; margin: 3px 0; }
    .part-spot--fig.has-tick, .part-spot--fig.is-lead { padding-right: 18px; }
    .part-spot--fig.has-tick::after { width: 12px; }
  }
</style>
